// Copied from flowsjs `packages/ui/kit/src/IconButton.tsx` (`@aiuirun/ui-kit`), which is not published.

import { Box, type SxProps, type Theme } from "@mui/material";
import type { MouseEvent, ReactNode } from "react";
import { useTokens } from "../theme/theme";

// The one icon-only button. Owns the `<button>` element, focus ring, disabled state, and — critically —
// the hover/active state as a **translucent state layer** (a low-alpha ink/danger wash), so it looks
// identical on any surface (card, cardSunken, the tabs bar). Callers pick `tone`; they never restyle the
// hover, which is what kept per-instance icon buttons drifting. `sx` is an escape hatch for genuine
// layout outliers (e.g. a full-height toolbar cell).
//
// The WASH is surface-agnostic; the ink is not. Hovering to `ink.primary` is near-black, which reads on
// paper and vanishes on the dark chrome of a viewer bar — so `on` says which ground the button sits on
// and the hover ink follows it.
//
// `active` is for a TOGGLE that is on: the button keeps its wash and its hover, and takes the accent
// ink, so a button showing a state still reads as a button.

export type IconButtonTone = "neutral" | "danger";

/** The ground the button sits on: light surfaces, or the viewer's dark chrome. */
export type IconButtonGround = "surface" | "chrome";

export type IconButtonProps = {
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  title?: string;
  tone?: IconButtonTone;
  on?: IconButtonGround;
  /** A toggle that is ON — accented, and still hoverable. */
  active?: boolean;
  size?: number; // square hit area
  disabled?: boolean;
  className?: string;
  children: ReactNode; // the icon
  sx?: SxProps<Theme>;
};

export function IconButton({
  onClick,
  title,
  tone = "neutral",
  on = "surface",
  active,
  size = 24,
  disabled,
  className,
  children,
  sx,
}: IconButtonProps) {
  const t = useTokens();
  const danger = tone === "danger";
  const chrome = on === "chrome";
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      {...(className ? { className } : {})}
      {...(title ? { title, "aria-label": title } : {})}
      sx={[
        {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
          width: size,
          height: size,
          p: 0,
          border: "none",
          borderRadius: `${t.radius.sm}px`,
          background: "none",
          color: active ? t.accent.base : chrome ? t.chrome.ink : t.ink.faint,
          cursor: "pointer",
          transition: "color .12s ease, background-color .12s ease",
          "&:hover": {
            color: danger
              ? t.semantic.danger
              : active
                ? t.accent.hover
                : chrome
                  ? t.chrome.inkHi
                  : t.ink.primary,
            bgcolor: danger ? t.state.hoverDanger : chrome ? t.chrome.raised : t.state.hover,
          },
          "&:active": { bgcolor: danger ? t.state.hoverDanger : t.state.active },
          "&:focus-visible": { outline: `2px solid ${t.accent.base}`, outlineOffset: "1px" },
          "&:disabled": {
            color: active ? t.accent.base : chrome ? t.chrome.ink : t.ink.faint,
            opacity: 0.45,
            cursor: "default",
            bgcolor: "transparent",
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Box>
  );
}
